/**
 * AuthContext — Supports both Org Users and Ops Users
 *
 * Determines user type from JWT response and provides separate login methods.
 * Stores active ops access token for org-scoped API calls.
 */

import { createContext, useContext, useState, useEffect, useRef, type ReactNode } from 'react';
import api from '../lib/api';
import { isReadOnlyOpsSession } from '../lib/opsWriteAccess';
import { IDENTITY_KEY, identityStamp, shouldReloadForStorageEvent } from '../lib/sessionIdentity';

interface OrgUser {
  type: 'org';
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  orgId: string;
  orgName: string;
  /**
   * Capability ids this user holds (spec 113 W1), from `/auth/me`. `can(id)`
   * reads this — the single source the UI shows/hides from. Optional so a stale
   * cached session without it degrades gracefully (treated as no capabilities).
   */
  capabilities?: string[];
  /**
   * OrgUser.primaryDepartment — inspection reopen is blocked for leasing /
   * accounting / compliance / front desk even when role is Admin/PM.
   */
  primaryDepartment?: string | null;
  /**
   * Present + `'email_link'` when this session was minted from an email
   * auto-login token (spec 102). Drives the non-dismissible "signed in via an
   * email link" banner in AppLayout. Absent for normal interactive sessions.
   */
  amr?: 'email_link';
  /**
   * Whether this person holds a password at all, from `/auth/me`. Drives the
   * change-password form: people who only ever sign in with an emailed code
   * have never had one, and must not be shown a "Current password" box.
   * Undefined on a session cached before this field existed — the form then
   * falls back to showing the box optional, exactly as it used to.
   */
  hasPassword?: boolean;
}

interface OpsUser {
  type: 'ops';
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string; // SUPER_ADMIN | OPS_AGENT
}

export type AppUser = OrgUser | OpsUser;

interface OpsOrgAccess {
  orgId: string;
  orgName: string;
  tokenId: string;
  accessToken: string;
  level: 'READ_ONLY' | 'WRITE';
  expiresAt: string;
}

/** One org the signed-in email belongs to — drives the sidebar account switcher. */
export interface OrgMembership {
  orgId: string;
  orgName: string;
  role: string;
  isCurrent: boolean;
}

interface AuthContextType {
  user: AppUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  opsLogin: (email: string, password: string) => Promise<void>;
  logout: () => void;
  /** Re-fetch the current user from the server (keeps the header in sync after a profile edit). */
  refreshUser: () => Promise<void>;
  isOpsUser: boolean;
  isOrgUser: boolean;
  opsOrgAccess: OpsOrgAccess | null;
  setOpsOrgAccess: (access: OpsOrgAccess | null) => void;
  /** Orgs the signed-in email can switch into (org users only). */
  organizations: OrgMembership[];
  /** Switch the active org: mints a new token for that org + reloads the app. */
  switchOrg: (orgId: string) => Promise<void>;
  /**
   * Capability check (spec 113 W1) — the ONE gate the UI should use for
   * show/hide/enable, instead of copied role literals. True for ops users with
   * org access (they bypass org role checks, mirroring the backend), and for org
   * users whose capability list includes `capability`.
   */
  can: (capability: string) => boolean;
  /**
   * `can`, plus the one thing `can` cannot express: an ops org-access token
   * carries a LEVEL, and a READ_ONLY one is refused every non-GET request by
   * the server ("Read-only ops access tokens cannot perform mutations"),
   * whatever capabilities the role would otherwise imply.
   *
   * So: **`can` for reads and navigation, `canWrite` for anything that
   * mutates.** Gating a write on `can` alone shows a read-only ops session
   * buttons that can only answer 403 — the same dead-end this layer exists to
   * remove. Making it a separate call rather than a flag each page remembers
   * to AND in is the point: forgetting is the failure mode.
   *
   * Identical to `can` for org users, so nothing changes for them.
   */
  canWrite: (capability: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [opsOrgAccess, setOpsOrgAccess] = useState<OpsOrgAccess | null>(null);
  const [organizations, setOrganizations] = useState<OrgMembership[]>([]);

  /**
   * WHO this tab believes it is — `type:id:orgId:role`, mirrored into
   * localStorage under `authIdentity` so sibling tabs can see it change.
   *
   * localStorage (the token) is shared across tabs; `user` (the capability list
   * the UI shows/hides from) is per-tab React state. Sign in as someone else in
   * a second tab and the first one keeps rendering the PREVIOUS role's buttons
   * while every request it makes already carries the new role's token — the
   * server then refuses them, so the affordance was a lie. Found by QA on
   * 2026-08-17 against Properties/Residents/Units, where a left-open PM tab
   * offered Add/Edit/Delete to a Maintenance and then a Viewer session.
   *
   * Role and orgId are in the stamp, not just the user id: the same person
   * re-graded, or switched into another org, is also a different capability set.
   */
  const identityRef = useRef<string | null>(localStorage.getItem(IDENTITY_KEY));

  /**
   * The chokepoint for every session change **inside this context**.
   *
   * It is NOT the only writer of the tokens. Several pages persist a session
   * themselves and then hard-navigate — `switchOrg` below, `LoginPage`'s
   * verify-code path, `OpsLoginPage`, `StaffMagicEnterPage`. Those are fine
   * without stamping: the navigation they fire re-boots the app, `/auth/me`
   * lands here, and the stamp is written one page load later. `AcceptInvitePage`
   * hard-navigates too, but still stamps itself: a sibling tab must not spend
   * even that one page load offering the previous account's buttons.
   * **If you add another path that persists a token without navigating, it must
   * stamp too**; an earlier version of this comment claimed every path came
   * through here, and the audit that found AcceptInvitePage had to disprove the
   * comment first.
   *
   * Writing the same value back is skipped deliberately: an unchanged write can
   * still raise a `storage` event in sibling tabs, and every tab re-reads
   * `/auth/me` on boot, so all of them would reload each other forever.
   */
  const adoptUser = (next: AppUser | null) => {
    setUser(next);
    const stamp = identityStamp(
      next ? { ...next, orgId: 'orgId' in next ? next.orgId : undefined } : null,
    );
    identityRef.current = stamp;
    if (stamp === null) {
      localStorage.removeItem(IDENTITY_KEY);
    } else if (localStorage.getItem(IDENTITY_KEY) !== stamp) {
      localStorage.setItem(IDENTITY_KEY, stamp);
    }
  };

  /**
   * Another tab signed in, signed out, or switched org. Reload rather than
   * quietly re-fetch: this tab's query cache and page state still hold rows read
   * as the previous identity, and a Viewer must not inherit a PM's loaded data.
   * `switchOrg` hard-loads for that same reason.
   *
   * A `storage` event only fires in the OTHER tabs, never the one that wrote —
   * so the tab doing the signing in cannot reload itself. Keying on
   * `authIdentity` rather than `accessToken` is what keeps a routine silent
   * token refresh (`lib/api.ts` rewrites `accessToken` on every 401 retry) from
   * reloading every other tab.
   */
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.storageArea && e.storageArea !== localStorage) return;
      if (!shouldReloadForStorageEvent(e.key, localStorage.getItem(IDENTITY_KEY), identityRef.current)) {
        return;
      }
      window.location.reload();
    };
    /**
     * The retry. `window.location.reload()` is REFUSABLE — a page with an armed
     * `beforeunload` (this app has one, the escalation-chain editor) shows
     * "Leave site?", and Cancel aborts it. The storage event does not come
     * again on its own, so without this the tab would sit in exactly the stale
     * state the guard exists to end, indefinitely. Audit 2026-08-17.
     *
     * `identityRef` is deliberately NOT advanced on the reload path: leaving it
     * stale is what keeps the comparison true so this re-check can fire.
     */
    const recheck = () => {
      if (document.visibilityState !== 'visible') return;
      if (localStorage.getItem(IDENTITY_KEY) === identityRef.current) return;
      window.location.reload();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', recheck);
    document.addEventListener('visibilitychange', recheck);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', recheck);
      document.removeEventListener('visibilitychange', recheck);
    };
  }, []);

  const loadOrganizations = async () => {
    try {
      const { data } = await api.get('/auth/my-organizations');
      setOrganizations(data.data.organizations || []);
    } catch {
      setOrganizations([]);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    const userType = localStorage.getItem('userType');
    if (!token) {
      setLoading(false);
      return;
    }

    const meEndpoint = userType === 'ops' ? '/ops/auth/me' : '/auth/me';
    api.get(meEndpoint)
      .then((res) => {
        const userData = res.data.data.user || res.data.data;
        if (userType === 'ops') {
          adoptUser({ type: 'ops', ...userData });
        } else {
          adoptUser({ type: 'org', ...userData });
          void loadOrganizations();
        }
      })
      /**
       * Clear ONLY on a real auth failure. A 500/502/timeout means the server
       * could not answer, not that the session died — and wiping storage here
       * broadcasts a `clear()` that now reloads every OTHER tab straight to the
       * login screen, losing whatever they held (a filled Create Work Order
       * form, a validated CSV import awaiting commit). One DB blip behind
       * `/auth/me` used to take the whole browser down with it. Audit
       * 2026-08-17. On a transient failure we leave the tokens alone: this tab
       * renders signed-out, and the next load retries with them intact.
       */
      .catch((err) => {
        if (err?.response?.status === 401) localStorage.clear();
      })
      .finally(() => setLoading(false));

    // Restore ops org access from session storage
    const savedAccess = sessionStorage.getItem('opsOrgAccess');
    if (savedAccess) {
      try {
        const parsed = JSON.parse(savedAccess);
        if (new Date(parsed.expiresAt) > new Date()) {
          setOpsOrgAccess(parsed);
        } else {
          sessionStorage.removeItem('opsOrgAccess');
        }
      } catch { /* ignore */ }
    }
  }, []);

  const login = async (email: string, password: string) => {
    const { data } = await api.post('/auth/login', { email, password });
    localStorage.setItem('accessToken', data.data.accessToken);
    localStorage.setItem('refreshToken', data.data.refreshToken);
    localStorage.setItem('userType', 'org');
    adoptUser({ type: 'org', ...data.data.user });
    void loadOrganizations();
  };

  /**
   * Switch to another org the signed-in email belongs to. Mints a fresh
   * token pair for that org, swaps the stored tokens, then hard-reloads to
   * the dashboard so every org-scoped query cache + component state is rebuilt
   * cleanly against the new org.
   */
  const switchOrg = async (orgId: string) => {
    const { data } = await api.post('/auth/switch-org', { orgId });
    localStorage.setItem('accessToken', data.data.accessToken);
    localStorage.setItem('refreshToken', data.data.refreshToken);
    localStorage.setItem('userType', 'org');
    window.location.assign('/');
  };

  const refreshUser = async () => {
    const userType = localStorage.getItem('userType');
    const meEndpoint = userType === 'ops' ? '/ops/auth/me' : '/auth/me';
    const res = await api.get(meEndpoint);
    const userData = res.data.data.user || res.data.data;
    if (userType === 'ops') {
      adoptUser({ type: 'ops', ...userData });
    } else {
      adoptUser({ type: 'org', ...userData });
    }
  };

  const opsLogin = async (email: string, password: string) => {
    const { data } = await api.post('/ops/auth/login', { email, password });
    localStorage.setItem('accessToken', data.data.accessToken);
    localStorage.setItem('userType', 'ops');
    adoptUser({ type: 'ops', ...data.data.user });
  };

  const logout = () => {
    const refreshToken = localStorage.getItem('refreshToken');
    const userType = localStorage.getItem('userType');
    if (refreshToken) {
      const logoutEndpoint = userType === 'ops' ? '/ops/auth/logout' : '/auth/logout';
      api.post(logoutEndpoint, { refreshToken }).catch(() => {});
    }
    localStorage.clear();
    sessionStorage.clear();
    adoptUser(null);
    setOpsOrgAccess(null);
    setOrganizations([]);
  };

  const handleSetOpsOrgAccess = (access: OpsOrgAccess | null) => {
    setOpsOrgAccess(access);
    if (access) {
      sessionStorage.setItem('opsOrgAccess', JSON.stringify(access));
    } else {
      sessionStorage.removeItem('opsOrgAccess');
    }
  };

  const can = (capability: string): boolean => {
    // Ops users acting inside an org bypass org role checks (mirrors the backend
    // authorize() ops-bypass); org users are gated by their capability list.
    if (user?.type === 'ops' && opsOrgAccess) return true;
    if (user?.type === 'org') {
      if (user.capabilities && user.capabilities.length > 0) {
        return user.capabilities.includes(capability);
      }
      // No capability list on the session (a frontend-ahead-of-backend deploy or
      // /auth/me shape drift). Fail OPEN for ORG_ADMIN only — an admin holds every
      // capability by definition, so this prevents their whole nav from silently
      // collapsing; every other role fails closed (safe). The backend still
      // enforces each action regardless of what the UI shows.
      return user.role === 'ORG_ADMIN';
    }
    return false;
  };

  // See `canWrite` on the context type. The level comparison is its own tested
  // module — the one spelling that fails closed on an unrecognised level.
  const opsReadOnly = isReadOnlyOpsSession({ userType: user?.type, level: opsOrgAccess?.level });
  const canWrite = (capability: string): boolean => !opsReadOnly && can(capability);

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      opsLogin,
      logout,
      refreshUser,
      isOpsUser: user?.type === 'ops',
      isOrgUser: user?.type === 'org',
      opsOrgAccess,
      setOpsOrgAccess: handleSetOpsOrgAccess,
      organizations,
      switchOrg,
      can,
      canWrite,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
